import { addDays, daysBetween, type Day } from "./day.js";
import type { Verdict } from "./answer.js";

/**
 * Leitner scheduling, per docs/ARCHITECTURE.md §6.
 *
 * Boxes are an internal concept. The interface calls them stars — never show
 * the word "Box" to a child (docs/UX.md §1).
 */
export const MAX_BOX = 6;

/** Days until a card in box N is due again. Index = box. */
export const BOX_INTERVAL_DAYS = [0, 1, 2, 4, 8, 16, 32] as const;

/** A word counts as "sitting" from box 3 upwards. */
export const LEARNED_BOX = 3;

/**
 * From this many lapses on, a word is treated as fragile: it keeps its box,
 * but it comes back in half the time. A word that has fallen over four times
 * is not as solid as the box claims, and the box alone cannot express that.
 */
export const FRAGILE_LAPSES = 4;

/**
 * How a card was asked. The app picks it from the Leitner box, card by card;
 * the child can only ask for tiles on a single card (docs/UX.md §4, issue #63).
 */
export type CardForm = "choice" | "type" | "match";

/**
 * Forms that put the answer on the screen carry a card to this box at most.
 * They show that a word is recognised, never that it can be produced, and in a
 * matching round the last pair is free (docs/PLAN.md §4, point 7).
 */
export const GIVEN_ANSWER_MAX_BOX = 1;

/** Only writing the word yourself moves a card past GIVEN_ANSWER_MAX_BOX. */
export function promotesFreely(form: CardForm): boolean {
  return form === "type";
}

/**
 * An answer that was on the screen for a word already past its first
 * encounter. The app only asks such a card on «Kacheln zeigen», so it is help,
 * and help counts like a second attempt: fewer XP, not «auf Anhieb richtig»
 * (docs/UX.md §4, issue #63). Derived from the box, not from a client flag, so
 * an older client with a remembered form switch gets the same rule.
 */
export function usedHelp(form: CardForm, boxBefore: number): boolean {
  return !promotesFreely(form) && boxBefore >= GIVEN_ANSWER_MAX_BOX;
}

/** The attempt an answer is worth: help on the first attempt counts as the second. */
export function countedAttempt(attempt: number, form: CardForm, boxBefore: number): number {
  return usedHelp(form, boxBefore) ? Math.max(attempt, 2) : attempt;
}

/**
 * How often a card that went wrong comes back within the same round. Three
 * attempts are enough: a word still wrong after that is in box 0 and due again
 * tomorrow, and an endless round punishes exactly the child who struggles most
 * (issue #45). It also keeps a round well inside the 100 results that
 * `practiceResultsInput` accepts.
 */
export const ROUND_REPEATS_MAX = 2;

/**
 * Exam mode only makes sense while the exam is still ahead. A list without a
 * date, or one whose exam is over, is practised like the long-term pool.
 */
export function roundMode(
  requested: "exam" | "longterm" | "weak",
  examDueDay: Day | null,
  today: Day,
): "exam" | "longterm" | "weak" {
  if (requested !== "exam") return requested;
  return examDueDay !== null && daysBetween(today, examDueDay) >= 0 ? "exam" : "longterm";
}

export interface TermState {
  box: number;
  dueDay: Day;
  reps: number;
  lapses: number;
  streak: number;
  learnedAt: number | null;
}

export interface ScheduleOptions {
  today: Day;
  nowSeconds: number;
  /** Set when the list has a due_date: intervals are squeezed to fit. */
  examDueDay?: Day | null;
  /** How the card was asked. Defaults to typing, the form without a cap. */
  form?: CardForm;
}

export function initialState(today: Day): TermState {
  return { box: 0, dueDay: today, reps: 0, lapses: 0, streak: 0, learnedAt: null };
}

/**
 * In exam mode we cannot use a 32-day interval for a test on Friday. Scale the
 * interval into the days that are actually left, but never below 1.
 */
function squeezeForExam(days: number, options: ScheduleOptions): number {
  if (days === 0) return 0;
  if (!options.examDueDay) return days;

  const remaining = daysBetween(options.today, options.examDueDay);
  if (remaining <= 0) return days; // exam is over, back to normal spacing
  const longest = BOX_INTERVAL_DAYS[MAX_BOX];
  const squeezed = Math.round((days / longest) * remaining);
  return Math.max(1, Math.min(days, squeezed));
}

/**
 * Days before the exam, its own day not included. roundMode still picks exam
 * cards on the exam day, but squeezeForExam treats that day as over and uses
 * full intervals, so early answers there would jump a word to a month away.
 */
function examAhead(options: ScheduleOptions): boolean {
  return !!options.examDueDay && daysBetween(options.today, options.examDueDay) > 0;
}

/** Half an interval, but never below a day. Box 0 keeps its "same round". */
function halved(days: number): number {
  return days <= 0 ? 0 : Math.max(1, Math.round(days / 2));
}

/**
 * Days until the card is due again. Two things shorten the nominal interval of
 * the box, and they stack: an answer that was only almost right, and a word
 * that keeps lapsing. The exam squeeze runs last, on whatever is left.
 */
function intervalFor(state: TermState, verdict: Verdict, options: ScheduleOptions): number {
  let days: number = BOX_INTERVAL_DAYS[Math.min(state.box, MAX_BOX)] ?? 0;
  // Almost right keeps the box (see applyAnswer) but not its full interval:
  // the word is nearly there, and meeting it again in 16 days wastes that.
  if (verdict === "almost") days = halved(days);
  if (state.lapses >= FRAGILE_LAPSES) days = halved(days);
  return squeezeForExam(days, options);
}

/**
 * Apply one answer. Pure: same input, same output, in the browser and in the
 * worker. Never reads the clock itself — the caller passes `today`.
 */
export function applyAnswer(
  previous: TermState,
  verdict: Verdict,
  options: ScheduleOptions,
): TermState {
  const next: TermState = { ...previous };
  next.reps = previous.reps + 1;

  // Right before the word was due shows it is still there, not that it lasted
  // the interval: box and due day stay, or an extra round in one evening would
  // carry a word up the boxes (issue #96). Wrong still counts. Exam mode, up
  // to the day before the exam, practises words ahead of their day on purpose
  // and squeezes instead.
  if (verdict !== "wrong" && daysBetween(options.today, previous.dueDay) > 0 && !examAhead(options)) {
    return next;
  }

  if (verdict === "wrong") {
    // Back to box 1, not 0: restarting from zero every time is discouraging.
    next.box = 1;
    next.lapses = previous.lapses + 1;
    next.streak = 0;
  } else if (verdict === "almost") {
    // Knew the word, missed an accent or a key. Keep the box; the shorter
    // interval is decided in intervalFor.
    next.streak = previous.streak + 1;
  } else {
    next.box = Math.min(previous.box + 1, MAX_BOX);
    if (!promotesFreely(options.form ?? "type")) {
      // The answer was on the screen. Such a round may carry a new word to box
      // 1, but it must not push a word further up -- and it must not push one
      // down either, because a right answer may never cost a box.
      next.box = Math.max(previous.box, Math.min(next.box, GIVEN_ANSWER_MAX_BOX));
    }
    next.streak = previous.streak + 1;
  }

  next.dueDay = addDays(options.today, intervalFor(next, verdict, options));
  if (next.learnedAt === null && next.box >= LEARNED_BOX) {
    next.learnedAt = options.nowSeconds;
  }
  return next;
}

/**
 * The form a box asks for: tiles for a word seen for the first time, typing
 * from the second encounter on.
 *
 * Box 1 must ask for typing, not tiles. Tiles cap a card at
 * GIVEN_ANSWER_MAX_BOX, so a box that asks for tiles and is already at the cap
 * is a dead end: the word would stay at box 1 forever, and no star and no exam
 * readiness could ever appear. The picked form alone has to be able to carry a
 * word all the way up.
 */
export function cardModeForBox(box: number): CardForm {
  return box <= 0 ? "choice" : "type";
}

/**
 * A word has two directions and both count. A direction that was never asked
 * is box 0, not "absent" -- otherwise the first round in the other direction
 * makes a word look worse than before it was practised (docs/STARS.md §1).
 */
export interface TermBoxes {
  ab: number;
  ba: number;
}

/** The weaker direction decides. Practising can only ever raise it. */
export function weakestBox(term: TermBoxes): number {
  return Math.min(term.ab, term.ba);
}

/** Stars for one word, per docs/STARS.md §3. */
export function termStars(term: TermBoxes): 0 | 1 | 2 | 3 {
  const box = weakestBox(term);
  if (box >= 4) return 3;
  if (box >= LEARNED_BOX) return 2;
  if (box >= 2) return 1;
  return 0;
}

/**
 * A word counts for an exam once both directions sit at LEARNED_BOX, the goal
 * docs/ARCHITECTURE.md §6 sets for the exam mode. That is exactly two stars.
 */
export function sitsForExam(term: TermBoxes): boolean {
  return weakestBox(term) >= LEARNED_BOX;
}

/**
 * How far one word has come towards sitting, between 0 and 1. Both directions
 * count and each is capped at LEARNED_BOX, the goal -- climbing past it does
 * not make a word more ready than ready (docs/STARS.md §8).
 *
 * This is the fine-grained view of the same data `sitsForExam` reads coarsely:
 * it moves on the first right answer, where the word count cannot move for
 * days.
 */
export function termProgress(term: TermBoxes): number {
  const towardsGoal = (box: number) => Math.min(Math.max(box, 0), LEARNED_BOX);
  return (towardsGoal(term.ab) + towardsGoal(term.ba)) / (2 * LEARNED_BOX);
}

/** The bar under «2 von 18 sitzen»: the mean progress of every word. */
export function listProgress(terms: readonly TermBoxes[]): number {
  if (terms.length === 0) return 0;
  return terms.reduce((sum, term) => sum + termProgress(term), 0) / terms.length;
}

/**
 * The same number from the aggregate the overview can afford: `steps` is
 * `SUM(MIN(box, LEARNED_BOX))` over both directions of every word, so a missing
 * row contributes nothing and needs no coalescing.
 */
export function listProgressFromSteps(steps: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, steps) / (total * 2 * LEARNED_BOX));
}

/**
 * How many words of a list carry at least one, two, three stars. The list
 * overview aggregates this in SQL instead of loading every box, so the
 * thresholds must live in one place -- see listStars below.
 */
export interface StarCounts {
  total: number;
  atLeast1: number;
  atLeast2: number;
  atLeast3: number;
}

/**
 * Stars for a whole list, per docs/STARS.md §3. The third star asks for 90 %,
 * not for everything: one stubborn word must not block it forever.
 */
export function listStarsFromCounts(counts: StarCounts): 0 | 1 | 2 | 3 {
  if (counts.total <= 0) return 0;
  if (counts.atLeast3 / counts.total >= 0.9) return 3;
  if (counts.atLeast2 / counts.total >= 0.8) return 2;
  if (counts.atLeast1 / counts.total >= 0.5) return 1;
  return 0;
}

/** Stars from the raw boxes, for callers that already hold every item. */
export function listStars(terms: readonly TermBoxes[]): 0 | 1 | 2 | 3 {
  const stars = terms.map(termStars);
  const atLeast = (min: number) => stars.filter((s) => s >= min).length;
  return listStarsFromCounts({
    total: terms.length,
    atLeast1: atLeast(1),
    atLeast2: atLeast(2),
    atLeast3: atLeast(3),
  });
}

/** Words ready for the exam, the number in «12 von 15 sitzen für Freitag». */
export function examReady(terms: readonly TermBoxes[]): number {
  return terms.filter(sitsForExam).length;
}
